import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { Menu, MenuItem } from '@mui/material'
import { useAppSelector } from 'store/hooks'
import { NotificationsMenu } from 'header/notificationsMenu'
import { AppServices } from 'middleware'
import { LucideIcon } from 'utils/lucideIcon'
import Feature from 'features/feature'
import { GlobalSearchTrigger } from 'globalSearch/globalSearchTrigger'
import { showErrorMessage } from 'uiComponents/message'
import i18n, { Language, LANGUAGES, switchLanguage, useTranslation } from 'translations'
import './topBar.scss'

interface TopBarProps {
    accountSlug: string | null
}

export function TopBar({ accountSlug }: TopBarProps) {
    const profile = useAppSelector((state) => state.profile.profile)
    const isReseller = useAppSelector((state) => !!state.auth.user?.resellerId)
    // Our own language selector (BUIL-819): lists each language in its own
    // name and reloads through switchLanguage, because many class components
    // call t() at import time and would otherwise show mixed languages.
    const { t } = useTranslation()
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState<HTMLElement | null>(null)
    const [switchingTo, setSwitchingTo] = useState<Language | null>(null)
    const currentLanguage = LANGUAGES.find((language) => language.code === i18n.resolvedLanguage) ?? LANGUAGES[0]
    const chooseLanguage = async (language: Language) => {
        if (language === currentLanguage.code) {
            setLanguageMenuAnchor(null)
            return
        }
        setSwitchingTo(language)
        try {
            // Reloads the page once the language is ready.
            await switchLanguage(language)
        } catch {
            showErrorMessage(t('sidebar.language.loadError'))
        } finally {
            setSwitchingTo(null)
            setLanguageMenuAnchor(null)
        }
    }

    const logClick = (path: string) => {
        AppServices.loggingService.logAction('navigation', {
            click_to: path,
            source: 'top_bar',
        })
    }

    // Profile menu: avatar + name as the button, Profile and Logout inside.
    const history = useHistory()
    const [profileMenuAnchor, setProfileMenuAnchor] = useState<HTMLElement | null>(null)
    const [helpMenuAnchor, setHelpMenuAnchor] = useState<HTMLElement | null>(null)
    const firstName = profile?.firstName?.trim() || ''
    const lastName = profile?.lastName?.trim() || ''
    const displayName = firstName ? `${firstName}${lastName ? ` ${lastName[0]}.` : ''}` : profile?.email || 'Profile'
    const openProfilePage = () => {
        setProfileMenuAnchor(null)
        logClick('/personal_settings/personal')
        history.push('/personal_settings/personal')
    }
    const logout = () => {
        setProfileMenuAnchor(null)
        AppServices.loginService.logout()
    }

    return (
        <div className="top-bar" id="top-bar">
            <div className="top-bar-row">
                {accountSlug && (
                    <Feature name="globalSearch" accountSlug={accountSlug}>
                        <div className="top-bar-search">
                            <GlobalSearchTrigger />
                        </div>
                    </Feature>
                )}
                <div className="top-bar-spacer" />
                <button
                    type="button"
                    className="top-bar-action top-bar-language"
                    aria-haspopup="menu"
                    aria-expanded={!!languageMenuAnchor}
                    aria-label={t('sidebar.language.triggerLabel', { language: currentLanguage.name })}
                    title={t('sidebar.language.triggerLabel', { language: currentLanguage.name })}
                    onClick={(event) => setLanguageMenuAnchor(event.currentTarget)}
                >
                    <span aria-hidden>{currentLanguage.code.toUpperCase()}</span>
                </button>
                <Menu
                    className="top-bar-language-menu"
                    anchorEl={languageMenuAnchor}
                    open={!!languageMenuAnchor}
                    onClose={() => setLanguageMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {LANGUAGES.map((language) => (
                        <MenuItem
                            key={language.code}
                            lang={language.code}
                            disabled={!!switchingTo}
                            selected={language.code === currentLanguage.code}
                            onClick={() => chooseLanguage(language.code)}
                        >
                            {language.name}
                            {language.code === currentLanguage.code && <LucideIcon iconName="Check" size={16} />}
                            {language.code === switchingTo && (
                                <LucideIcon iconName="LoaderCircle" size={16} className="spinning" />
                            )}
                        </MenuItem>
                    ))}
                </Menu>
                <button
                    type="button"
                    className="top-bar-action"
                    aria-haspopup="menu"
                    aria-expanded={!!helpMenuAnchor}
                    aria-label={t('header.topBar.help')}
                    title={t('header.topBar.help')}
                    onClick={(event) => setHelpMenuAnchor(event.currentTarget)}
                >
                    <LucideIcon iconName="CircleHelp" size={18} />
                </button>
                <Menu
                    className="top-bar-profile-menu"
                    anchorEl={helpMenuAnchor}
                    open={!!helpMenuAnchor}
                    onClose={() => setHelpMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {[
                        {
                            label: t('header.topBar.knowledgeBase'),
                            href: 'https://support.convious.com',
                            icon: 'BookOpen' as const,
                        },
                        {
                            label: t('header.topBar.support'),
                            href: 'https://convious.com/support',
                            icon: 'LifeBuoy' as const,
                        },
                    ].map(({ label, href, icon }) => (
                        <MenuItem
                            key={href}
                            component="a"
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                                setHelpMenuAnchor(null)
                                logClick(href)
                            }}
                        >
                            <LucideIcon iconName={icon} size={16} />
                            {label}
                        </MenuItem>
                    ))}
                </Menu>
                {!isReseller && accountSlug && <NotificationsMenu accountSlug={accountSlug} />}
                <button
                    type="button"
                    className={classNames('top-bar-action', { bubble: profile && !profile.emailConfirmed })}
                    aria-haspopup="menu"
                    aria-expanded={!!profileMenuAnchor}
                    aria-label={displayName}
                    title={displayName}
                    onClick={(event) => setProfileMenuAnchor(event.currentTarget)}
                >
                    {profile?.avatarUrl ? (
                        <img className="top-bar-profile-photo" src={profile.avatarUrl} alt="" />
                    ) : (
                        <LucideIcon iconName="CircleUserRound" size={18} />
                    )}
                </button>
                <Menu
                    className="top-bar-profile-menu"
                    anchorEl={profileMenuAnchor}
                    open={!!profileMenuAnchor}
                    onClose={() => setProfileMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <li className="top-bar-profile-menu-name notranslate">{displayName}</li>
                    <MenuItem onClick={openProfilePage}>
                        <LucideIcon iconName="UserPen" size={16} />
                        {t('header.topBar.profile')}
                    </MenuItem>
                    <MenuItem onClick={logout}>
                        <LucideIcon iconName="LogOut" size={16} />
                        {t('header.topBar.logout')}
                    </MenuItem>
                </Menu>
            </div>
        </div>
    )
}

export default TopBar
